import { entity, legalPaths } from '../entity';
import {
  P, H3, Ul, Callout, Ext, Tok,
} from '../blocks';
import type { LegalDoc } from '../types';

// Términos del Servicio (español).
//
// Traducción de terms.en.tsx — mismos ids de sección, misma numeración, mismo
// orden. Si editas uno, edita el otro.
//
// Para consumidores residentes en México esta versión es la que rige (§21), así
// que no es una traducción de cortesía. Dos puntos que no deben perderse al
// editar:
//   - §6 incorpora las Normas de la Comunidad por referencia. Esa frase es lo
//     único que les da fuerza contractual.
//   - §17.6 excluye expresamente a los consumidores de México y de Quebec del
//     arbitraje y de la renuncia a la acción colectiva. Los derechos de la
//     LFPC son irrenunciables y PROFECO sigue siendo competente.

export const termsEs: LegalDoc = {
  lang: 'es',
  title: 'Términos del Servicio',
  updatedLabel: `Última actualización: ${entity.effectiveDate}`,
  contentsLabel: 'Contenido',
  altHref: legalPaths.terms,
  altLabel: 'View in English',
  sections: [
    {
      id: 'about',
      heading: '1. Estos Términos',
      body: (
        <>
          <P>
            Tribys es una aplicación para encontrar grupos de personas con
            intereses afines y después conocerlas en persona. Estos Términos
            constituyen el acuerdo entre tú y{' '}
            <Tok value={entity.legalName} />, una sociedad de responsabilidad
            limitada constituida en <Tok value={entity.formedIn} />, Estados
            Unidos (&quot;Tribys&quot;, &quot;nosotros&quot;). Aplican cuando creas
            una cuenta, usas la aplicación o usas cualquier página de tribys.com.
          </P>
          <P>
            Al crear una cuenta aceptas estos Términos. Si no los aceptas, no uses
            Tribys.
          </P>
          <P>
            Otros dos documentos forman parte de este acuerdo, y conviene que leas
            ambos:
          </P>
          <Ul
            items={[
              <>Nuestras <a href={legalPaths.guidelinesEs}>Normas de la Comunidad</a>, que establecen lo que puedes y lo que no puedes hacer en Tribys.</>,
              <>Nuestro <a href={legalPaths.privacyEs}>Aviso de Privacidad</a>, que explica qué datos recabamos y para qué. Si usas la verificación de identidad, aplica además el <a href={legalPaths.biometricEs}>Aviso sobre Datos Biométricos y Datos de Salud del Consumidor</a>.</>,
            ]}
          />
          <Callout>
            <strong>
              La sección 17 exige que la mayoría de las controversias se resuelvan
              mediante arbitraje individual y renuncia a tu derecho de participar
              en una acción colectiva.
            </strong>{' '}
            Incluye un paso previo de resolución informal de 60 días y un derecho
            de 30 días para rechazar el arbitraje por completo.{' '}
            <strong>
              Si resides en México o en Quebec, la sección 17 no te aplica.
            </strong>{' '}
            Por favor léela.
          </Callout>
        </>
      ),
    },
    {
      id: 'eligibility',
      heading: '2. Debes tener 18 años o más',
      body: (
        <>
          <P>
            <strong>Tribys es solo para personas adultas.</strong> Debes tener al
            menos 18 años para crear una cuenta. No existe una versión de Tribys
            para menores de edad y no la ofrecemos, porque la aplicación se
            construyó alrededor de conocer a desconocidos cara a cara.
          </P>
          <P>
            Verificamos tu fecha de nacimiento al registrarte y rechazamos a
            cualquier persona menor de 18 años. Si descubrimos que una cuenta
            pertenece a un menor, la eliminamos. Si crees que un menor está usando
            Tribys, reporta la cuenta en la aplicación o escribe a{' '}
            <Tok value={entity.supportEmail} />.
          </P>
          <P>
            Tampoco debes tener impedimento legal para usar Tribys conforme a la
            legislación que te resulte aplicable, ni haber sido expulsado de Tribys
            con anterioridad.
          </P>
        </>
      ),
    },
    {
      id: 'account',
      heading: '3. Tu cuenta',
      body: (
        <>
          <P>
            Necesitas una cuenta para usar Tribys. Al crearla te comprometes a dar
            información veraz y a mantenerla actualizada, en particular tu nombre,
            tu fecha de nacimiento y tu foto de perfil. Tribys solo funciona si las
            personas son quienes dicen ser.
          </P>
          <Ul
            items={[
              'Una cuenta por persona. No crees una segunda cuenta ni una cuenta para alguien más.',
              'Tu cuenta es solo tuya. No la compartas, no la vendas, no la rentes ni la transfieras.',
              'Mantén tus credenciales seguras y avísanos pronto si crees que alguien más tiene acceso.',
              'No crees una cuenta nueva para evadir una suspensión.',
            ]}
          />
          <P>
            Eres responsable de lo que ocurra en tu cuenta. Si iniciaste sesión con
            Google o Apple, mantener segura esa cuenta forma parte de
            mantener segura tu cuenta de Tribys.
          </P>
        </>
      ),
    },
    {
      id: 'meeting',
      heading: '4. Conocer personas en la vida real — por favor lee esto',
      body: (
        <>
          <Callout>
            <strong>
              Tribys te presenta a personas desconocidas y te ayuda a organizar
              encuentros presenciales. No hacemos verificación de antecedentes, no
              evaluamos el carácter ni las intenciones de nadie, no estamos
              presentes en los encuentros y no somos parte de ningún plan que
              acuerdes. Tú decides a quién ver, dónde y si vas.
            </strong>
          </Callout>
          <P>
            Preferimos decirlo de frente en lugar de esconderlo. Conocer a personas
            que no conoces implica un riesgo real, y ninguna función de esta
            aplicación lo elimina. La verificación de identidad confirma que un
            documento y un rostro coinciden; no dice nada sobre si una persona es
            segura, honesta o agradable. Los reportes y las suspensiones son
            reactivos: ocurren después de que algo salió mal.
          </P>
          <P>
            <strong>Asumes la responsabilidad de tu propia seguridad.</strong> En
            la máxima medida que permita la ley, asumes los riesgos de conocer a
            otros miembros y de asistir a cualquier evento o lugar que encuentres
            a través de Tribys, y aceptas que no somos responsables de la conducta
            de otros miembros, ni en línea ni fuera de línea. Las secciones 14 y 15
            establecen los límites de nuestra responsabilidad.
          </P>
          <P>Algunas cosas que te pedimos hacer:</P>
          <Ul
            items={[
              'Reúnanse en lugares públicos, sobre todo la primera vez. Por algo la mayoría de nuestros Spots son lugares públicos.',
              'Dile a alguien de confianza a dónde vas y con quién.',
              'Mantén la conversación dentro de la aplicación hasta que te sientas cómodo de moverla a otro lado.',
              'No envíes dinero a nadie que hayas conocido por Tribys, y trata cualquier petición de dinero como un fraude.',
              'Confía en tu instinto y vete si algo se siente mal. No le debes una explicación a nadie.',
              'Reporta a la persona después. Así es como encontramos a quienes lo hacen de forma repetida.',
            ]}
          />
          <P>
            Si estás en peligro inmediato, contacta primero a los servicios de
            emergencia de tu localidad. Después avísanos, para que podamos actuar
            sobre la cuenta.
          </P>
        </>
      ),
    },
    {
      id: 'verification',
      heading: '5. Qué significa la insignia de verificación',
      body: (
        <>
          <P>
            La verificación de identidad es opcional. Si la completas, nuestro
            proveedor lee tu identificación oficial y comprueba que está presente
            una persona viva que coincide con ella, y obtienes una insignia de
            verificación. Las fotos de perfil posteriores se comparan contra la
            imagen de referencia para que nadie pueda verificarse con su propia
            identificación y después poner el rostro de otra persona en el perfil.
          </P>
          <P>
            <strong>
              La insignia significa una sola cosa: que el documento y el rostro
              coincidieron en el momento de la revisión.
            </strong>{' '}
            No es una carta de recomendación, ni una revisión de antecedentes
            penales, ni una calificación de seguridad, ni una promesa sobre el
            comportamiento de nadie. Trata a un miembro verificado con exactamente
            la misma cautela con la que tratarías a cualquier desconocido.
          </P>
          <P>
            La verificación también puede fallar o acertar por error: es una
            revisión automatizada sobre una imagen. Consulta el{' '}
            <a href={legalPaths.biometricEs}>
              Aviso sobre Datos Biométricos y Datos de Salud del Consumidor
            </a>{' '}
            para saber qué recabamos, cuánto tiempo lo conservamos y cómo retirar
            tu consentimiento, y la sección 6 del{' '}
            <a href={legalPaths.privacyEs}>Aviso de Privacidad</a> para saber cómo
            pedir que una persona revise una decisión automatizada.
          </P>
        </>
      ),
    },
    {
      id: 'rules',
      heading: '6. Reglas de uso',
      body: (
        <>
          <P>
            <strong>
              Nuestras{' '}
              <a href={legalPaths.guidelinesEs}>Normas de la Comunidad</a> forman
              parte de estos Términos, y violarlas constituye un incumplimiento de
              este acuerdo.
            </strong>{' '}
            Cubren conducta, contenido y seguridad, y podemos actualizarlas cuando
            aparezcan problemas nuevos. Léelas: son más cortas que este documento y
            es más probable que afecten tu día.
          </P>
          <P>Además, te comprometes a no:</P>
          <Ul
            items={[
              'usar Tribys para algo ilícito, ni para organizar algo ilícito;',
              'extraer, rastrear, recolectar o recopilar de forma masiva información, perfiles, fotos o ubicaciones de otros miembros, por cualquier medio, incluidos los automatizados;',
              'realizar ingeniería inversa, descompilar o intentar extraer nuestro código fuente, salvo cuando la ley lo permita expresamente;',
              'interferir con nuestros sistemas, sobrecargarlos o sondearlos, ni intentar evadir límites de uso, autenticación o cualquier medida de seguridad;',
              'usar bots, scripts o herramientas automatizadas para crear cuentas, enviar mensajes, unirte a grupos o interactuar con otros miembros;',
              'acceder a Tribys por medios distintos a nuestra aplicación y nuestro sitio web, ni construir un servicio competidor o derivado con nuestros datos;',
              'suplantar a nadie, incluidos nosotros, ni tergiversar tu relación con alguna persona u organización;',
              'revender, sublicenciar o explotar comercialmente cualquier parte de Tribys, ni usarla para anunciar o promover un negocio salvo en lo que permitan las Normas de la Comunidad;',
              'subir nada que contenga software malicioso, ni usar Tribys para distribuirlo;',
              'ayudar a alguien más a hacer cualquiera de las anteriores.',
            ]}
          />
        </>
      ),
    },
    {
      id: 'your-content',
      heading: '7. Tu contenido',
      body: (
        <>
          <P>
            &quot;Tu contenido&quot; es todo lo que aportas a Tribys: tu perfil y
            tus fotos, los tribys que creas, los Spots que agregas, los mensajes
            que envías, los intereses que creas, los reportes que presentas y
            cualquier otra cosa que envíes.
          </P>
          <H3>La propiedad sigue siendo tuya</H3>
          <P>
            Tu contenido sigue siendo tuyo. No reclamamos su propiedad y no lo
            vendemos.
          </P>
          <H3>El permiso que nos otorgas</H3>
          <P>
            Para operar Tribys necesitamos permiso para almacenar y mostrar tu
            contenido. Nos otorgas una licencia mundial, no exclusiva y libre de
            regalías para alojar, almacenar, guardar en caché, reproducir,
            redimensionar, recortar, publicar, mostrar públicamente y distribuir tu
            contenido, y para sublicenciar esos derechos a los proveedores de
            alojamiento y de red de distribución de contenido que utilizamos, con
            el fin de operar, proteger y mejorar Tribys. No usamos tu contenido con
            fines publicitarios, y no operamos servicios de publicidad ni de
            analítica.
          </P>
          <P>
            Esto incluye mostrar tu nombre visible y tu foto de perfil en las
            vistas previas de enlaces cuando tú u otro miembro comparte el enlace
            de un triby, un Spot o un perfil; esas vistas previas puede leerlas
            cualquiera que tenga el enlace. Consulta el{' '}
            <a href={legalPaths.privacyEs}>Aviso de Privacidad</a> para saber qué
            páginas son indexables por buscadores y cuáles no.
          </P>
          <H3>Cuánto dura ese permiso</H3>
          <P>
            En general termina cuando eliminas el contenido o tu cuenta. Hay una
            excepción, y vale la pena entenderla:
          </P>
          <Callout>
            Para los <strong>Spots que creas, los tribys que creas y los mensajes
            que ya enviaste a otros miembros</strong>, la licencia es perpetua e
            irrevocable. Otras personas dependen de ese contenido: un Spot puede
            ser la sede de un grupo que sigue reuniéndose ahí, y una conversación
            pertenece a todos los que participaron en ella. Eliminar tu cuenta
            quita tu nombre y tus identificadores de ese contenido, y vacía el
            texto de tus mensajes, pero el Spot o el triby permanece para quienes
            lo usan. La sección sobre eliminación del{' '}
            <a href={legalPaths.privacyEs}>Aviso de Privacidad</a> describe con
            precisión qué se elimina y qué permanece.
          </Callout>
          <H3>Lo que nos garantizas sobre él</H3>
          <P>Al enviar contenido confirmas que:</P>
          <Ul
            items={[
              'es tuyo, o cuentas con todos los derechos y permisos necesarios para enviarlo y para otorgarnos la licencia anterior;',
              'no infringe derechos de autor, marcas, secretos industriales, derechos sobre bases de datos, derechos de imagen ni derechos de privacidad de nadie;',
              <><strong>tomaste tú las fotografías que subes, o cuentas con el permiso de quien las tomó.</strong> Una foto que encontraste en internet, en redes sociales, en un sitio de reseñas o en el sitio web de un negocio pertenece a quien la tomó, no al establecimiento y no a ti.</>,
              'cualquier persona identificable en una foto que subas aceptó aparecer en ella;',
              'es veraz, y no es engañoso respecto de quién eres o de qué es un lugar o un evento;',
              'no viola estos Términos ni las Normas de la Comunidad.',
            ]}
          />
          <P>
            Eres responsable de tu contenido y de las consecuencias de enviarlo. La
            sección 16 explica qué significa eso si un tercero presenta una
            reclamación.
          </P>
          <H3>Comentarios y sugerencias</H3>
          <P>
            Si nos envías sugerencias sobre Tribys, podemos usarlas libremente y
            sin deberte nada. Por favor no nos envíes nada que consideres
            confidencial.
          </P>
        </>
      ),
    },
    {
      id: 'spots',
      heading: '8. Spots, negocios y eventos',
      body: (
        <>
          <P>
            Un Spot es un lugar o un evento en el mapa de Tribys. Los Spots son{' '}
            <strong>creados por los miembros, no por nosotros</strong>. No los
            verificamos, no estamos afiliados a los lugares o eventos listados y no
            los respaldamos. Un Spot puede ser inexacto, estar desactualizado,
            estar cerrado o simplemente estar mal. Confirma antes de ir, y trata los
            horarios, las direcciones y las fechas como el mejor cálculo de un
            miembro y no como un hecho.
          </P>
          <P>
            Como los Spots describen negocios y eventos reales, es aquí donde con
            más probabilidad aparecen derechos de terceros. Si creas un Spot, eres
            responsable de él.
          </P>
          <H3>Lo que sí está bien</H3>
          <P>
            Identificar un lugar por su nombre real y dar su dirección, su
            ubicación, sus horarios y una descripción objetiva. Así es como un mapa
            resulta útil, y referirse a un negocio por su nombre para decir dónde
            está no requiere el permiso de nadie.
          </P>
          <H3>Lo que no</H3>
          <Ul
            items={[
              <><strong>Fotos que no tomaste.</strong> Este es el problema más común. No subas fotos tomadas del sitio web de un negocio o de sus redes sociales, de Google, de un sitio de reseñas o de un kit de prensa. Toma las tuyas, o deja el Spot sin foto.</>,
              <><strong>Logotipos, arte promocional, carteles de eventos, volantes y menús.</strong> Son diseños de alguien. No los subas como imágenes de un Spot.</>,
              <><strong>Insinuar una relación que no existe.</strong> No presentes un Spot como oficial, patrocinado, respaldado o afiliado a un negocio, establecimiento, marca u organizador salvo que genuinamente lo sea.</>,
              <><strong>Usar un Spot como anuncio.</strong> Copiar textos de marketing, agregar promociones o descuentos, o poner datos de contacto para dirigir clientes hacia ti no es para lo que sirven los Spots. Consulta las Normas de la Comunidad.</>,
              <><strong>Copiar una base de datos de directorios.</strong> No importes Spots de forma masiva desde otro mapa, directorio o servicio de listados.</>,
            ]}
          />
          <H3>Si es tu negocio o tu evento</H3>
          <P>
            Puedes agregarlo, y puedes subir tus propias fotos. Todo lo demás en
            estos Términos y en las Normas de la Comunidad sigue aplicando: un Spot
            es un lugar en un mapa, no un espacio publicitario.
          </P>
          <H3>Si alguien listó tu negocio, tu establecimiento o tu evento</H3>
          <P>
            Reporta el Spot en la aplicación usando el motivo{' '}
            <strong>propiedad intelectual</strong>, o escribe a{' '}
            <Tok value={entity.copyrightEmail} /> con el enlace del Spot y el
            problema. Lo revisaremos y, cuando corresponda, lo corregiremos o lo
            retiraremos. Para derechos de autor en particular, la sección 9
            establece el procedimiento formal y es la vía más rápida.
          </P>
        </>
      ),
    },
    {
      id: 'copyright',
      heading: '9. Reclamaciones de derechos de autor y de marcas',
      body: (
        <>
          <P>
            Atendemos las notificaciones de presunta infracción de derechos de
            autor, y cancelamos las cuentas de quienes infringen de forma
            reiterada.
          </P>
          <H3>Agente designado</H3>
          <P>Envía las notificaciones de derechos de autor a nuestro agente:</P>
          <P>
            Copyright Agent
            <br />
            <Tok value={entity.legalName} />
            <br />
            <Tok value={entity.address} />
            <br />
            <Tok value={entity.copyrightEmail} />
          </P>
          <H3>Qué debe contener una notificación</H3>
          <P>
            Para ser eficaz conforme al 17 U.S.C. §512(c)(3), tu notificación debe
            incluir todo lo siguiente:
          </P>
          <Ul
            items={[
              'tu firma física o electrónica;',
              'la identificación de la obra protegida que consideras infringida;',
              'la identificación del material que consideras infractor, con suficiente detalle para localizarlo — lo mejor es el enlace al Spot, al triby o al perfil;',
              'tu nombre, domicilio, teléfono y correo electrónico;',
              'una declaración de que tienes la creencia de buena fe de que el uso no está autorizado por el titular, su representante o la ley;',
              'una declaración de que la información de tu notificación es exacta y de que, bajo pena de perjurio, eres el titular o estás autorizado para actuar en su nombre.',
            ]}
          />
          <P>
            Una notificación incompleta puede retrasar nuestra respuesta. Ten en
            cuenta que{' '}
            <strong>
              afirmar a sabiendas que un material es infractor puede generarte
              responsabilidad por daños
            </strong>{' '}
            conforme al 17 U.S.C. §512(f), incluidos nuestros gastos legales.
          </P>
          <H3>Qué hacemos</H3>
          <P>
            Si la notificación es válida, retiramos o inhabilitamos el acceso al
            material con prontitud y avisamos al miembro que lo publicó,
            trasladándole tu notificación. También podemos retirar material que
            consideremos infractor sin esperar una notificación.
          </P>
          <H3>Contranotificación</H3>
          <P>
            Si tu material fue retirado y consideras que fue un error o una
            identificación equivocada, puedes enviar una contranotificación al
            domicilio anterior, con tu firma, la identificación del material
            retirado y del lugar donde aparecía, una declaración bajo pena de
            perjurio de que tienes la creencia de buena fe de que se retiró por
            error o identificación equivocada, y tu nombre, domicilio y teléfono,
            aceptando la jurisdicción del tribunal federal de tu distrito. Si
            recibimos una contranotificación válida, la trasladamos a quien
            reclamó y podemos restablecer el material entre 10 y 14 días hábiles
            después, salvo que nos informen que iniciaron una acción judicial.
          </P>
          <H3>Infractores reincidentes</H3>
          <P>
            Registramos las notificaciones de infracción contra cada cuenta. Una
            cuenta objeto de notificaciones válidas reiteradas será suspendida y
            después cancelada de forma permanente. Aplicamos el mismo criterio a
            quienes suben repetidamente fotos sobre las que no tienen derechos,
            haya o no una notificación formal.
          </P>
          <H3>Marcas</H3>
          <P>
            Envía las reclamaciones de marcas al mismo domicilio, indicando la
            marca, su registro si lo tienes, y dónde aparece el problema. Recuerda
            que usar el nombre de un negocio para identificar su ubicación
            generalmente está permitido, así que dinos específicamente qué resulta
            engañoso: un logotipo usado como foto de un Spot, o un Spot presentado
            como oficial, son los problemas reales habituales.
          </P>
          <H3>Fuera de Estados Unidos</H3>
          <Ul
            items={[
              <><strong>México.</strong> Envía las notificaciones conforme a la Ley Federal del Derecho de Autor al domicilio anterior, con la misma información indicada arriba, y actuaremos sobre ellas.</>,
              <><strong>Canadá.</strong> Canadá opera un sistema de aviso y aviso conforme a su Copyright Act. Si nos envías una notificación válida sobre un miembro canadiense, nuestra obligación es trasladársela y conservar el registro; no nos obliga a retirar el material. Aun así podemos retirarlo conforme a estos Términos.</>,
            ]}
          />
        </>
      ),
    },
    {
      id: 'our-content',
      heading: '10. Nuestra aplicación y nuestra marca',
      body: (
        <>
          <P>
            Tribys — la aplicación, el sitio web, el software, el diseño, los
            textos que escribimos, el nombre Tribys y el logotipo de Tribys — nos
            pertenece a nosotros y a nuestros licenciantes, y está protegido por
            legislación de derechos de autor, marcas y otras.
          </P>
          <P>
            Te otorgamos una licencia personal, limitada, revocable, no exclusiva y
            no transferible para instalar y usar la aplicación Tribys en
            dispositivos que controles, y para usar nuestro sitio web, para tu uso
            personal y no comercial, siempre que cumplas estos Términos. Eso es
            todo lo que otorgamos. Cualquier derecho no otorgado expresamente queda
            reservado.
          </P>
          <P>
            No uses nuestro nombre, logotipo o identidad de marca de forma que
            sugiera que te respaldamos, y no elimines ni oscurezcas ningún aviso de
            propiedad.
          </P>
        </>
      ),
    },
    {
      id: 'paid',
      heading: '11. Funciones de pago',
      body: (
        <>
          <P>
            Algunas funciones de Tribys cuestan dinero. Tribys Pro se vende como
            una suscripción con renovación automática de 1, 3, 6 o 12 meses, o
            como un pase de una semana que no se renueva. Un Pro Triby es una
            compra única que permite a una cuenta gratuita realizar una sola
            acción Pro. Los precios se muestran en la aplicación antes de que
            compres.
          </P>
          <H3>A quién le pagas en realidad</H3>
          <P>
            <strong>
              Las compras las procesan Apple o Google, no nosotros.
            </strong>{' '}
            Ellos son el comerciante registrado. Nunca vemos ni almacenamos los
            datos de tu tarjeta. Sus términos rigen la transacción misma, y tu
            historial de compras y tu método de pago están en tu cuenta de App
            Store o de Google Play.
          </P>
          <H3>Las suscripciones se renuevan automáticamente</H3>
          <P>
            Una suscripción se renueva por el mismo periodo al precio vigente hasta
            que la canceles.{' '}
            <strong>Cancela desde App Store o Google Play</strong>: eliminar la
            aplicación Tribys o tu cuenta de Tribys no cancela una suscripción, y
            nosotros no podemos cancelarla por ti. Cancela al menos 24 horas antes
            de que termine el periodo, y conservas tus beneficios hasta que
            termine.
          </P>
          <H3>Reembolsos</H3>
          <P>
            Los reembolsos los gestionan Apple o Google conforme a sus políticas,
            así que solicítalos con ellos. Podemos ayudarte a entender qué pasó,
            pero no podemos emitir un reembolso por una compra hecha en la tienda.
            Cuando la ley de tu lugar de residencia te dé un derecho de
            cancelación o de reembolso, ese derecho aplica y nada de lo aquí
            señalado lo limita; en México esto incluye tus derechos conforme a la
            Ley Federal de Protección al Consumidor.
          </P>
          <H3>Pases, Pro Tribys y recompensas</H3>
          <P>
            El pase de una semana te da Pro durante siete días desde la compra y
            después termina por sí solo; nada se renueva. Un Pro Triby es un
            crédito que se consume cuando se completa la acción Pro: unirte a un
            Pro triby o crearlo, unirte como miembro oculto o superar tu límite
            mensual. El crédito vuelve a tu cuenta si nosotros retiramos ese
            triby, o si tú lo creaste y nadie más se había unido a la hora
            programada, salvo que Apple o Google ya hayan reembolsado la compra.
            Las recompensas por recomendación dependen de que tú y la persona que
            recomendaste cumplan las condiciones mostradas al compartir el código;
            podemos modificar o terminar un programa de recomendaciones, y podemos
            retener una recompensa que consideremos obtenida mediante cuentas
            falsas u otros abusos.
          </P>
          <H3>Cambios y beneficios no usados</H3>
          <P>
            Podemos cambiar los precios, y podemos agregar, modificar o retirar
            funciones de pago. En una suscripción, un cambio de precio surte
            efecto en tu siguiente renovación y te avisaremos antes para que puedas
            cancelar. Los beneficios de pago no tienen valor en efectivo, no son
            propiedad y no son transferibles. Si cancelamos tu cuenta por violar
            estos Términos, no tienes derecho al reembolso de beneficios no usados.
          </P>
        </>
      ),
    },
    {
      id: 'third-party',
      heading: '12. Servicios de terceros',
      body: (
        <>
          <P>
            Tribys se conecta con servicios que no controlamos, y usarlos implica
            aceptar sus términos además de los nuestros:
          </P>
          <Ul
            items={[
              'Iniciar sesión con Google o Apple.',
              'Vincular Spotify para mostrar tus artistas más escuchados en tu perfil.',
              'Mapas e indicaciones, que pueden abrir la aplicación de mapas que elijas.',
              'Agregar un triby al calendario de tu dispositivo, que puede sincronizarse con el servicio que use tu calendario.',
              'Compartir un enlace, que lo envía a donde tú decidas.',
            ]}
          />
          <P>
            No somos responsables de esos servicios, de su contenido ni de su
            disponibilidad. Si desconectas alguno, o si deja de funcionar, partes
            de Tribys pueden dejar de funcionar también.
          </P>
          <P>
            Los enlaces a otros sitios no son recomendaciones, y no tenemos control
            sobre su contenido.
          </P>
        </>
      ),
    },
    {
      id: 'moderation',
      heading: '13. Moderación, suspensión y cancelación',
      body: (
        <>
          <P>
            Usamos una combinación de revisiones automatizadas y revisión humana
            para hacer cumplir estos Términos y las Normas de la Comunidad. Las{' '}
            <a href={legalPaths.guidelinesEs}>Normas de la Comunidad</a> describen
            qué está automatizado, qué no, las medidas que tomamos y cómo apelar
            una decisión, incluido cómo llegar a una persona.
          </P>
          <P>
            <strong>
              Podemos retirar contenido, y podemos suspender o cancelar una cuenta.
            </strong>{' '}
            Lo hacemos cuando alguien viola estos Términos o las Normas, cuando la
            ley nos obliga, cuando una cuenta parece comprometida o fraudulenta, o
            cuando razonablemente consideramos que es necesario para proteger a
            otros miembros. Cuando sea razonable te diremos por qué, y un caso
            grave o ilícito puede implicar actuar de inmediato y sin aviso.
          </P>
          <P>
            También podemos dejar de ofrecer Tribys, o parte de ella, en tu país o
            por completo. Si lo hacemos, daremos un aviso razonable cuando sea
            posible.
          </P>
          <P>
            Puedes eliminar tu cuenta en cualquier momento desde Configuración. Hay
            un plazo de 30 días en el que iniciar sesión la restaura; después se
            anonimiza como se describe en el{' '}
            <a href={legalPaths.privacyEs}>Aviso de Privacidad</a>. Eliminar tu
            cuenta no cancela una suscripción de la tienda: consulta la sección 11.
          </P>
          <P>
            Las secciones que deben subsistir al término de este acuerdo subsisten:
            7 (la licencia que otorgaste, en la medida señalada), 9, 10, 14, 15,
            16, 17, 18 y 21.
          </P>
        </>
      ),
    },
    {
      id: 'disclaimers',
      heading: '14. Tribys se ofrece tal como está',
      body: (
        <>
          <P>
            Trabajamos mucho en Tribys, pero no podemos prometer que sea perfecta.
            En la máxima medida que permita la ley, Tribys se proporciona
            &quot;tal como está&quot; y &quot;según disponibilidad&quot;, y
            rechazamos toda garantía, expresa o implícita, incluidas las garantías
            implícitas de comerciabilidad, idoneidad para un fin determinado y no
            infracción, así como cualquier garantía derivada de la práctica
            comercial o del uso.
          </P>
          <P>En particular, no prometemos que:</P>
          <Ul
            items={[
              'Tribys sea ininterrumpida, segura, oportuna o libre de errores;',
              'los miembros sean quienes dicen ser, ni que nuestra verificación sea siempre correcta;',
              'otros miembros se comporten de forma lícita, honesta o decente, en línea o en persona;',
              'un Spot exista, esté abierto, sea exacto o sea seguro;',
              'un triby tenga miembros, o que un evento vaya a ocurrir;',
              'la moderación automatizada detecte todo lo dañino, o que nunca retire algo que no debía retirar.',
            ]}
          />
          <P>
            <strong>
              Nada en estos Términos excluye ni limita algo que no pueda excluirse
              o limitarse legalmente.
            </strong>{' '}
            Algunas jurisdicciones no permiten la exclusión de ciertas garantías,
            por lo que partes de esta sección podrían no aplicarte. Si eres
            consumidor, tus derechos legales permanecen intactos, incluidos tus
            derechos conforme a la Ley Federal de Protección al Consumidor en
            México y, en Canadá, conforme a la legislación provincial de protección
            al consumidor aplicable.
          </P>
        </>
      ),
    },
    {
      id: 'liability',
      heading: '15. Límites de nuestra responsabilidad',
      body: (
        <>
          <P>
            En la máxima medida que permita la ley, ni Tribys ni sus socios,
            administradores, empleados, contratistas o proveedores serán
            responsables por daños indirectos, incidentales, especiales,
            consecuenciales, ejemplares o punitivos, ni por lucro cesante, pérdida
            de datos, pérdida de reputación o interrupción de actividades, que
            deriven de o se relacionen con tu uso de Tribys, sea que la reclamación
            se funde en contrato, responsabilidad civil, negligencia,
            responsabilidad objetiva, ley o cualquier otra causa, e incluso si se
            nos advirtió de la posibilidad de tales daños.
          </P>
          <P>
            En la máxima medida que permita la ley, nuestra responsabilidad total
            por todas las reclamaciones relacionadas con Tribys se limita a la
            mayor de{' '}
            <strong>
              la cantidad que nos hayas pagado en los 12 meses anteriores al hecho
              que dio origen a la reclamación, o cien dólares estadounidenses
              (US$100)
            </strong>.
          </P>
          <P>
            Esto incluye, específicamente, cualquier reclamación derivada de la
            conducta de otro miembro, de un encuentro organizado a través de
            Tribys, o de algo que ocurra en un Spot o en un evento. La sección 4
            explica por qué: no estamos ahí y no somos parte del acuerdo.
          </P>
          <P>
            Estos límites son parte esencial del acuerdo entre nosotros: Tribys no
            se ofrecería en estos términos sin ellos. No aplican a
            responsabilidades que no puedan limitarse legalmente, incluidas, cuando
            corresponda, la muerte o las lesiones personales causadas por nuestra
            negligencia, nuestro propio dolo o declaraciones fraudulentas, o
            nuestra negligencia grave o conducta intencional indebida. Si eres
            consumidor en México o Canadá, tus derechos legales permanecen
            intactos.
          </P>
        </>
      ),
    },
    {
      id: 'indemnity',
      heading: '16. Tu responsabilidad por reclamaciones sobre tu contenido y tu conducta',
      body: (
        <>
          <P>
            Aceptas indemnizar y mantener a salvo a Tribys y a sus socios,
            administradores, empleados y contratistas frente a cualquier
            reclamación, demanda, pérdida, responsabilidad, daño, costo o gasto,
            incluidos honorarios legales razonables, que derive de o se relacione
            con:
          </P>
          <Ul
            items={[
              <><strong>tu contenido</strong>, incluida cualquier reclamación de que infringe derechos de autor, marcas, derechos de imagen o de privacidad, por ejemplo una fotografía, un logotipo, un cartel o una descripción que agregaste a un Spot;</>,
              'tu uso de Tribys y cualquier cosa que hagas a través de ella;',
              'tu incumplimiento de estos Términos, de las Normas de la Comunidad o de la ley;',
              'cualquier controversia entre tú y otro miembro, incluido lo que ocurra en un encuentro, evento o Spot;',
              'cualquier reclamación de un negocio, establecimiento, organizador o titular de derechos sobre un Spot que hayas creado.',
            ]}
          />
          <P>
            Podemos asumir la defensa de cualquier reclamación de este tipo a tu
            costa y, si lo hacemos, aceptas cooperar con nosotros. No puedes
            transigir una reclamación en términos que nos impongan alguna
            obligación sin nuestro consentimiento por escrito.
          </P>
          <P>
            Si eres consumidor, esta sección aplica únicamente en la medida en que
            lo permita la ley de tu lugar de residencia.
          </P>
        </>
      ),
    },
    {
      id: 'disputes',
      heading: '17. Resolución de controversias',
      body: (
        <>
          <Callout>
            <strong>Por favor lee esta sección con atención.</strong> Cambia la
            forma en que se resuelven las reclamaciones legales entre nosotros. Te
            exige intentar primero una resolución informal, después llevar la
            mayoría de las reclamaciones a arbitraje individual en lugar de a los
            tribunales, e implica que renuncias al derecho a un jurado y al derecho
            de participar en una acción colectiva. Tienes 30 días para rechazarla.{' '}
            <strong>
              No aplica en absoluto si resides en México o en Quebec.
            </strong>
          </Callout>
          <H3>17.1 Primero, habla con nosotros — este paso es obligatorio</H3>
          <P>
            La mayoría de los problemas se pueden resolver, y preferimos
            resolverlos que discutirlos. Antes de que cualquiera de las dos partes
            inicie un arbitraje o una demanda, quien tenga la queja debe enviar a
            la otra un <strong>Aviso de Controversia</strong> por escrito.
          </P>
          <P>
            Envía el tuyo a <Tok value={entity.legalEmail} /> con &quot;Aviso de
            Controversia&quot; en el asunto, o por correo postal al domicilio de la
            sección 22. Debe ser individual, estar firmado por ti (o por ti y tu
            abogado si lo tienes) e incluir tu nombre, el correo electrónico de tu
            cuenta de Tribys, una descripción del problema y qué quieres que
            hagamos. Nosotros enviaremos el nuestro al correo electrónico de tu
            cuenta.
          </P>
          <P>
            <strong>
              A partir de ahí tenemos 60 días para intentar resolverlo contigo, y
              completar este paso es requisito para iniciar un arbitraje o acudir a
              los tribunales.
            </strong>{' '}
            Cualquiera de las dos partes puede solicitar una conferencia
            individual, telefónica o por video, durante ese periodo, y ambas
            participaremos de buena fe. Cualquier plazo de prescripción, y
            cualquier plazo para pagar una cuota de presentación, queda suspendido
            mientras esto ocurre. Una solicitud de arbitraje presentada antes de
            concluir este paso es inválida, y ninguna de las partes podrá recuperar
            las cuotas de haberla presentado. Cualquiera de las dos puede pedir a
            un tribunal que haga cumplir este párrafo.
          </P>
          <H3>17.2 Después, arbitraje individual</H3>
          <P>
            Si no lo resolvemos en 60 días, cualquiera de las dos partes puede
            iniciar un arbitraje vinculante, y el arbitraje será la vía exclusiva
            para resolver la controversia, salvo las excepciones de 17.3. Esto
            aplica a cualquier controversia o reclamación relacionada con Tribys,
            con estos Términos, con tu cuenta o con tu relación con nosotros, sin
            importar cuándo surgió, incluidas las reclamaciones sobre publicidad y
            las que surjan después de que tu cuenta termine.
          </P>
          <P>
            El arbitraje será administrado por la{' '}
            <strong>American Arbitration Association</strong> conforme a sus
            Consumer Arbitration Rules y, cuando apliquen, sus Mass Arbitration
            Supplementary Rules, con las modificaciones de esta sección. Las reglas
            y formatos están en <Ext href="https://www.adr.org">adr.org</Ext>.
            Decidirá un árbitro único, que aplicará la ley y respetará cualquier
            privilegio que la ley reconozca. El árbitro puede otorgar cualquier
            medida que un tribunal pudiera otorgarte individualmente, incluido
            cualquier remedio legal.
          </P>
          <P>
            El arbitraje se llevará a cabo en el condado donde vives, o de forma
            remota por teléfono, video o por escrito si lo prefieres. Las reglas de
            la AAA determinan quién paga qué y, conforme a ellas, nosotros pagamos
            la mayor parte de las cuotas en casos de consumidores. Nada en esta
            sección obliga a ninguna de las partes a mantener confidencial la
            existencia, el contenido o el resultado de un arbitraje.
          </P>
          <P>
            La decisión de un árbitro obliga únicamente a las partes de ese
            arbitraje. No constituye precedente y no puede usarse para decidir la
            reclamación de otra persona.
          </P>
          <H3>17.3 Qué queda fuera</H3>
          <Ul
            items={[
              <><strong>Reclamaciones menores.</strong> Cualquiera de las dos partes puede presentar una reclamación individual ante un tribunal de cuantía menor, si ahí procede.</>,
              <><strong>Propiedad intelectual y uso indebido.</strong> Cualquiera de las dos partes puede solicitar a un tribunal una medida cautelar u otra medida equitativa en materia de propiedad intelectual, o por acceso no autorizado, extracción de datos o interferencia con Tribys.</>,
              'Cualquier asunto que la ley disponga que no puede someterse a arbitraje. En particular, esta sección no renuncia al derecho de presentar una reclamación ante una autoridad, ni de participar en un procedimiento ante ella.',
            ]}
          />
          <H3>17.4 Sin acciones colectivas</H3>
          <P>
            <strong>
              El arbitraje y cualquier procedimiento judicial permitido por 17.3
              serán individuales. Ninguna de las dos partes podrá presentar una
              reclamación como demandante o miembro de una clase en un
              procedimiento colectivo, consolidado, representativo o de acción
              popular, y el árbitro no podrá consolidar reclamaciones ni presidir
              ninguna forma de procedimiento colectivo o representativo.
            </strong>{' '}
            Tú y nosotros renunciamos a cualquier derecho a un juicio con jurado.
          </P>
          <P>
            <strong>
              Este párrafo no es separable del resto de esta sección.
            </strong>{' '}
            Si se determina inejecutable, total o parcialmente, respecto de alguna
            reclamación, o si un tribunal decide que una reclamación puede
            tramitarse de forma colectiva, consolidada, representativa o de acción
            popular, entonces el arbitraje no aplica a esa reclamación, y esa
            reclamación —y solo esa— será resuelta por un tribunal conforme a la
            sección 18. El arbitraje colectivo no está disponible conforme a estos
            Términos bajo ninguna circunstancia. El resto de esta sección subsiste.
          </P>
          <H3>17.5 Puedes rechazarlo</H3>
          <P>
            Puedes rechazar el arbitraje por completo. Escribe a{' '}
            <Tok value={entity.legalEmail} /> con &quot;Arbitration
            Opt-Out&quot; en el asunto, incluyendo tu nombre y el correo
            electrónico de tu cuenta de Tribys, dentro de los{' '}
            <strong>30 días siguientes a tu primera aceptación de estos
            Términos</strong>, o bien, si después modificamos esta sección de forma
            que te afecte, dentro de los 30 días siguientes a esa modificación. Si
            la rechazas, las secciones 17.2 y 17.4 no te aplican y las
            controversias van a los tribunales conforme a la sección 18. Rechazarla
            no tiene ningún otro efecto: no cerraremos tu cuenta ni te trataremos
            distinto por hacerlo.
          </P>
          <H3>17.6 Si resides en México o en Quebec</H3>
          <Ul
            items={[
              <><strong>México.</strong> Tus derechos conforme a la Ley Federal de Protección al Consumidor son irrenunciables, y los procedimientos de conciliación y arbitraje de PROFECO siguen estando a tu disposición, diga lo que diga esta sección. Si eres consumidor residente en México, las secciones 17.2 y 17.4 no te aplican.</>,
              <><strong>Quebec.</strong> El artículo 11.1 de la Consumer Protection Act prohíbe obligar a un consumidor a arbitrar y prohíbe privarlo de una acción colectiva. Si eres consumidor residente en Quebec, las secciones 17.2 y 17.4 no te aplican en absoluto, y conservas íntegro tu derecho de acudir a los tribunales y de ser miembro de una acción colectiva. Solo aplica 17.1, y únicamente como una solicitud de que nos contactes primero.</>,
              <>En otros lugares, si la ley de tu residencia hace inejecutable alguna parte de esta sección frente a un consumidor, esa parte no te aplica y el resto sigue aplicando.</>,
            ]}
          />
          <H3>17.7 Modificaciones</H3>
          <P>
            Si modificamos esta sección, la modificación no aplica a una
            controversia respecto de la cual ya se envió un Aviso de Controversia,
            o respecto de la cual ya se inició un arbitraje o un procedimiento
            judicial.
          </P>
        </>
      ),
    },
    {
      id: 'law',
      heading: '18. Ley aplicable y dónde se presentan las reclamaciones',
      body: (
        <>
          <P>
            Estos Términos se rigen por las leyes del estado de{' '}
            <Tok value={entity.formedIn} />, Estados Unidos, y por la legislación
            federal estadounidense cuando resulte aplicable, sin atender a las
            normas de conflicto de leyes. La Federal Arbitration Act rige la
            sección 17.
          </P>
          <P>
            Para cualquier reclamación que no se resuelva en arbitraje —porque la
            sección 17 no la cubre, porque la rechazaste o porque está excluida—
            los tribunales estatales y federales ubicados en{' '}
            <Tok value={entity.formedIn} /> tienen jurisdicción exclusiva, y tú y
            nosotros aceptamos ese fuero.
          </P>
          <P>
            <strong>Los consumidores conservan sus protecciones locales.</strong>{' '}
            Si eres consumidor, esta sección no te priva de la protección de las
            normas imperativas del país o la provincia donde resides, ni del
            derecho de iniciar procedimientos ahí cuando esas normas te lo
            otorguen. Los consumidores residentes en México pueden acudir ante
            PROFECO o ante los tribunales mexicanos competentes, y los residentes
            en Quebec pueden iniciar procedimientos en Quebec.
          </P>
        </>
      ),
    },
    {
      id: 'changes',
      heading: '19. Cambios a estos Términos',
      body: (
        <>
          <P>
            Podemos modificar estos Términos, por ejemplo cuando agregamos una
            función o cuando cambia la ley. La fecha al inicio de esta página
            siempre indica la versión vigente.
          </P>
          <P>
            Si un cambio afecta materialmente tus derechos, te avisaremos antes de
            que entre en vigor, mediante un aviso en la aplicación, por correo
            electrónico o ambos, y te daremos un plazo razonable para leerlo.
            Seguir usando Tribys después de que un cambio entre en vigor significa
            que aceptas los nuevos Términos. Si no los aceptas, deja de usar Tribys
            y elimina tu cuenta.
          </P>
          <P>
            Los cambios a la sección 17 están sujetos a 17.5 y 17.7: obtienes un
            nuevo plazo de 30 días para rechazarla, y un cambio nunca aplica de
            forma retroactiva a una controversia ya iniciada. Las Normas de la
            Comunidad pueden actualizarse por separado, y la sección 6 las mantiene
            como parte de este acuerdo conforme se actualicen.
          </P>
        </>
      ),
    },
    {
      id: 'apple',
      heading: '20. Si obtuviste Tribys en la App Store de Apple',
      body: (
        <>
          <P>
            Estos términos los exige Apple y aplican además de todo lo anterior:
          </P>
          <Ul
            items={[
              'Estos Términos son únicamente entre tú y nosotros, no con Apple, y Apple no es responsable de Tribys ni de su contenido.',
              'Tu licencia de uso de Tribys se limita al uso no transferible en un dispositivo de marca Apple que poseas o controles, conforme lo permitan los Términos de Servicio de la App Store.',
              'Apple no tiene obligación alguna de proporcionar mantenimiento o soporte para Tribys.',
              'Si Tribys no cumple alguna garantía aplicable, puedes notificarlo a Apple y Apple te reembolsará el precio de compra, si lo hubo. En la máxima medida que permita la ley, Apple no tiene ninguna otra obligación de garantía respecto de Tribys.',
              'Apple no es responsable de atender ninguna reclamación tuya o de un tercero relacionada con Tribys, incluidas las reclamaciones por responsabilidad del producto, las de que Tribys no cumple un requisito legal o regulatorio, y las derivadas de legislación de protección al consumidor o de privacidad.',
              'Si un tercero reclama que Tribys o tu uso de ella infringe su propiedad intelectual, Apple no es responsable de la investigación, defensa, transacción ni cumplimiento de esa reclamación.',
              'Declaras que no te encuentras en un país sujeto a embargo del Gobierno de Estados Unidos o designado como país que apoya el terrorismo, y que no figuras en ninguna lista del Gobierno de Estados Unidos de partes prohibidas o restringidas.',
              'Apple y sus subsidiarias son terceros beneficiarios de esta sección y pueden exigir su cumplimiento frente a ti.',
            ]}
          />
        </>
      ),
    },
    {
      id: 'general',
      heading: '21. Lo demás',
      body: (
        <>
          <Ul
            items={[
              <><strong>Acuerdo completo.</strong> Estos Términos, junto con las Normas de la Comunidad, el Aviso de Privacidad y el Aviso sobre Datos Biométricos y Datos de Salud del Consumidor, constituyen el acuerdo completo entre nosotros respecto de Tribys y sustituyen cualquier acuerdo previo.</>,
              <><strong>Divisibilidad.</strong> Si alguna disposición resulta inejecutable, se limitará o eliminará en la medida mínima necesaria y el resto permanecerá en vigor, salvo la sección 17.4, que establece lo que ocurre si ella cae.</>,
              <><strong>Sin renuncia.</strong> Si no exigimos algo de inmediato, no hemos renunciado al derecho de exigirlo después.</>,
              <><strong>Cesión.</strong> No puedes transferir estos Términos ni tu cuenta. Nosotros podemos transferirlos a una afiliada, o en relación con una fusión, adquisición o venta de activos, sin que tus derechos se vean afectados.</>,
              <><strong>Notificaciones.</strong> Podemos contactarte en la aplicación, mediante notificaciones push o en el correo electrónico de tu cuenta. Las notificaciones formales hacia nosotros van a <Tok value={entity.legalEmail} /> y al domicilio postal indicado abajo; las notificaciones sobre una controversia legal deben además seguir 17.1.</>,
              <><strong>Sin representación.</strong> Estos Términos no crean una sociedad, una relación laboral, una agencia ni una empresa conjunta entre nosotros.</>,
              <><strong>Fuerza mayor.</strong> Ninguna de las partes es responsable por un incumplimiento causado por algo fuera de su control razonable.</>,
              <><strong>Idioma.</strong> Este documento también se publica en inglés. Para consumidores residentes en México rige la versión en español; en otros lugares, si ambas difieren, rige la versión en inglés.</>,
            ]}
          />
        </>
      ),
    },
    {
      id: 'contact',
      heading: '22. Contacto',
      body: (
        <>
          <P>
            <Tok value={entity.legalName} />
            <br />
            <Tok value={entity.address} />
          </P>
          <Ul
            items={[
              <>Soporte general, reportes de seguridad y apelaciones: <Tok value={entity.supportEmail} /></>,
              <>Notificaciones legales, Avisos de Controversia y rechazo del arbitraje: <Tok value={entity.legalEmail} /></>,
              <>Privacidad y solicitudes sobre tus datos: <Tok value={entity.privacyEmail} /></>,
              <>Notificaciones de derechos de autor y marcas: <Tok value={entity.copyrightEmail} /></>,
            ]}
          />
        </>
      ),
    },
  ],
};
